import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { Button } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles, theme } from 'app/theme'
import { EXTERNAL_URL_REGEX } from 'app/utils/customFunctions'
import { useVocabularyData } from 'app/utils/vocabulary'
import { easeInOut, motion } from 'framer-motion'
import { Link } from 'gatsby'
import React, { memo, useState } from 'react'

export interface Props {
  description?: string
  image?: ImageProps
  index?: number
  languageCode: string
  style?: any
  title: string
  URL?: string
}

export const Item = memo(function Item({
  description,
  image,
  index,
  languageCode,
  style,
  title,
  URL,
}: Props) {
  if (!title) {
    return null
  }

  const externalURL = URL && EXTERNAL_URL_REGEX.test(URL)
  const [onHover, setOnHover] = useState(false)

  const componentDOM = (
    <>
      <Background style={{ y: style }}>
        {image ? <Image {...image} /> : null}
      </Background>

      <Wrapper>
        <Number>{(index || 0).toString().padStart(2, '0')}</Number>

        {title ? <Title tag="h3" text={title} /> : null}

        <Text
          initial={{ height: 0, marginTop: 0, opacity: 0, y: 90 }}
          animate={
            onHover
              ? { height: 'auto', marginTop: 24, opacity: 1, y: 0 }
              : { height: 0, marginTop: 0, opacity: 0, y: 90 }
          }
          transition={{
            duration: 0.4,
            ease: easeInOut,
          }}
        >
          {description ? (
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          ) : null}

          <CTA
            label={useVocabularyData('explore', languageCode)}
            noActions
            variant="flat-invert"
          />
        </Text>
      </Wrapper>
    </>
  )

  return (
    <>
      {externalURL ? (
        <ExternalLink
          href={URL}
          onMouseEnter={() => setOnHover(true)}
          onMouseLeave={() => setOnHover(false)}
          rel="noopener"
          target="_blank"
        >
          {componentDOM}
        </ExternalLink>
      ) : (
        <Container
          onMouseEnter={() => setOnHover(true)}
          onMouseLeave={() => setOnHover(false)}
          to={URL || ''}
        >
          {componentDOM}
        </Container>
      )}
    </>
  )
})

const Background = styled(motion.div)`
  height: calc(100% + 6.25rem);
  position: relative;

  @media (max-width: 1199px) {
    height: 100%;
    transform: none !important;
  }
`

const Wrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 2.25rem;
  left: 2.25rem;
  transform: translateY(-50%);
  z-index: 2;
`

const Number = styled.div`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  margin-bottom: 0.75rem;

  @media (max-width: 1199px) {
    margin-bottom: 1.5rem;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.medium}

  max-width: 22.5rem;
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  margin: auto;

  @media (max-width: 1199px) {
    max-width: 12.5rem;
  }
`

const Text = styled(motion.div)`
  @media (max-width: 1199px) {
    height: auto !important;
    opacity: 1 !important;
    transform: none !important;
  }
`

const Description = styled.div`
  ${styles.description}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};

  @media (max-width: 1199px) {
    display: none;
  }
`

const CTA = styled(Button)`
  margin-top: 1.5rem;
`

const Style = css`
  aspect-ratio: 1;
  overflow: hidden;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${theme.colors.variants.neutralDark1};
    opacity: 0.5;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }
  &:hover {
    ${CTA} {
      &:before {
        left: 100%;
        transition-delay: 0.3s;
      }
      &:after {
        right: 0;
        transition-delay: 0.6s;
      }
    }
  }

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    display: block;
    width: 100%;
    margin-top: 2rem;
    &:first-of-type {
      margin-top: 0;
    }
  }
`

const ExternalLink = styled.a`
  ${Style}
`

const Container = styled(Link)`
  ${Style}
`
